import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { client } from './lib/apolloClient';
import { AuthProvider } from './context/AuthContext';
import { useIsMobile } from './hooks/useIsMobile';

// Desktop Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Page from './pages/Page';
import Category from './pages/Category';
import Dashboard from './pages/Dashboard';
import PostAd from './pages/PostAd';
import MyAds from './pages/MyAds';
import Favorites from './pages/Favorites';
import Profile from './pages/Profile';
import PublicProfile from './pages/PublicProfile';
import AdDetail from './pages/AdDetail';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';
import Pricing from './pages/Pricing';
import SafetyTips from './pages/SafetyTips';
import PostingRules from './pages/PostingRules';
import FAQ from './pages/FAQ';
import ContactUs from './pages/ContactUs';
import Boost from './pages/Boost';
import BoostPayment from './pages/BoostPayment';
import AdminBoostManagement from './pages/AdminBoostManagement';
import Stories from './pages/Stories';
import Search from './pages/Search';
import Messages from './pages/Messages';

// Mobile Pages
import MobileHome from './pages/MobileHome';
import MobileLogin from './pages/Mobile/MobileLogin';
import MobileRegister from './pages/Mobile/MobileRegister';
import MobileCategories from './pages/Mobile/MobileCategories';
import MobileCategory from './pages/Mobile/MobileCategory';
import MobileDashboard from './pages/Mobile/MobileDashboard';
import MobileProfile from './pages/Mobile/MobileProfile';
import MobilePostAd from './pages/Mobile/MobilePostAd';
import MobileAdDetail from './pages/Mobile/MobileAdDetail';
import MobileAlerts from './pages/Mobile/MobileAlerts';
import MobileMenu from './pages/Mobile/MobileMenu';

const MobileRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<MobileHome />} />
    <Route path="/login" element={<MobileLogin />} />
    <Route path="/register" element={<MobileRegister />} />
    <Route path="/forgot-password" element={<MobileLogin />} /> {/* Reuse login for now */}
    <Route path="/page/:slug" element={<MobileHome />} /> {/* Redirect to home for now */}
    <Route path="/categories" element={<MobileCategories />} />
    <Route path="/search" element={<Search />} />
    <Route path="/category/:slug" element={<MobileCategory />} />
    <Route path="/notifications" element={<MobileAlerts />} />
    <Route path="/alerts" element={<MobileAlerts />} />
    <Route path="/messages" element={<Messages />} />
    <Route path="/menu" element={<MobileMenu />} />
    <Route path="/dashboard" element={<MobileDashboard />} />
    <Route path="/profile" element={<MobileProfile />} />
    <Route path="/profile/:username" element={<PublicProfile />} />
    <Route path="/post-ad" element={<MobilePostAd />} />
    <Route path="/my-ads" element={<MobileDashboard />} /> {/* Dashboard handles this */}
    <Route path="/favorites" element={<MobileDashboard />} /> {/* Dashboard handles this */}
    <Route path="/ad/:slug" element={<MobileAdDetail />} />
    <Route path="/stories" element={<Stories />} />
    {/* Legal & Help Pages */}
    <Route path="/terms" element={<Terms />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/cookies" element={<CookiePolicy />} />
    <Route path="/disclaimer" element={<Disclaimer />} />
    <Route path="/pricing" element={<Pricing />} />
    <Route path="/safety-tips" element={<SafetyTips />} />
    <Route path="/posting-rules" element={<PostingRules />} />
    <Route path="/faq" element={<FAQ />} />
    <Route path="/contact-us" element={<ContactUs />} />
    {/* 404 Catch-all - must be last */}
    <Route path="*" element={<MobileHome />} />
  </Routes>
);

const DesktopRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<Home />} />
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/page/:slug" element={<Page />} />
    <Route path="/search" element={<Search />} />
    <Route path="/category/:slug" element={<Category />} />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/post-ad" element={<PostAd />} />
    <Route path="/my-ads" element={<MyAds />} />
    <Route path="/favorites" element={<Favorites />} />
    <Route path="/profile" element={<Profile />} />
    <Route path="/profile/:username" element={<PublicProfile />} />
    <Route path="/ad/:slug" element={<AdDetail />} />
    {/* Boost Pages */}
    <Route path="/boost" element={<Boost />} />
    <Route path="/boost/payment/:id" element={<BoostPayment />} />
    <Route path="/admin/boosts" element={<AdminBoostManagement />} />
    {/* Story Ads */}
    <Route path="/stories" element={<Stories />} />
    {/* Messaging */}
    <Route path="/messages" element={<Messages />} />
    {/* Legal & Help Pages */}
    <Route path="/terms" element={<Terms />} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/cookies" element={<CookiePolicy />} />
    <Route path="/disclaimer" element={<Disclaimer />} />
    <Route path="/pricing" element={<Pricing />} />
    <Route path="/safety-tips" element={<SafetyTips />} />
    <Route path="/posting-rules" element={<PostingRules />} />
    <Route path="/faq" element={<FAQ />} />
    <Route path="/contact-us" element={<ContactUs />} />
    {/* 404 Catch-all - must be last */}
    <Route path="*" element={<Home />} />
  </Routes>
);

const AppContent: React.FC = () => {
  const isMobile = useIsMobile();

  return (
    <Router>
      {isMobile ? <MobileRoutes /> : <DesktopRoutes />}
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ApolloProvider>
  );
};

export default App;
